import { db, storage } from '../firebase';
import { collection, addDoc, serverTimestamp, query, where, getDocs, orderBy, doc, getDoc, updateDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { 
  triggerNewTaskNotification,
  triggerFulfillmentNotification,
  triggerCommunityPostNotification
} from './notificationTriggerService';

/**
 * Clean data to remove undefined and null values that Firestore doesn't support
 * @param {Object} data - The data to clean
 * @returns {Object} - The cleaned data
 */
const cleanDataForFirestore = (data) => {
  const cleaned = {};
  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined && value !== null) {
      cleaned[key] = value;
    } else {
      console.log(`⚠️ Removing ${value === undefined ? 'undefined' : 'null'} field: ${key}`);
    }
  }
  console.log(`🧹 Cleaned data: removed ${Object.keys(data).length - Object.keys(cleaned).length} undefined/null fields`);
  return cleaned;
};

/**
 * Upload file for task attachment
 * @param {File} file - The file to upload
 * @param {string} taskId - The task ID
 * @returns {Promise<Object>} - The file information
 */
export const uploadTaskFile = async (file, taskId) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to upload files');
    }

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'image/png',
      'image/gif',
      'text/plain'
    ];

    if (!allowedTypes.includes(file.type)) {
      throw new Error('File type not allowed. Please upload PDF, DOC, DOCX, XLS, XLSX, images, or text files.');
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error('File size too large. Maximum size is 10MB.');
    }

    // Create unique filename
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const storageRef = ref(storage, `tasks/${taskId}/${fileName}`);

    // Upload file
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    return {
      fileUrl: downloadURL,
      fileName: file.name,
      fileSize: file.size,
      uploadedAt: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error uploading task file:', error);
    throw error;
  }
};

/**
 * Create a new task and trigger notification
 * @param {Object} taskData - The task data
 * @returns {Promise<Object>} - The created task with ID
 */
export const createTask = async (taskData) => {
  try {
    console.log('🔍 Original taskData:', taskData);
    
    // Ensure assignees is always an array and filter out any undefined values
    let assignees = [];
    if (taskData.assignees && Array.isArray(taskData.assignees)) {
      assignees = taskData.assignees.filter(id => id !== undefined && id !== null && id !== '');
    } else if (taskData.assignee) {
      assignees = [taskData.assignee];
    }

    const task = {
      title: taskData.title,
      description: taskData.description,
      assignees: assignees,
      createdBy: taskData.createdBy,
      dueDate: taskData.dueDate || null, // Explicitly set to null if undefined
      priority: taskData.priority || 'medium',
      status: taskData.status || 'open',
      category: taskData.category || 'general',
      completed: taskData.completed || false,
      accountId: taskData.accountId,
      companyId: taskData.companyId ?? null,
      companyName: taskData.companyName ?? null,
      files: taskData.files || [], // Ensure files is always an array
      createdAt: serverTimestamp()
    };

    console.log('🔍 Task object before cleaning:', task);

    // Clean the data to remove any undefined values
    const cleanedTask = cleanDataForFirestore(task);
    
    console.log('🔍 Cleaned task object:', cleanedTask);

    // Add task to Firestore
    const taskRef = await addDoc(collection(db, 'tasks'), cleanedTask);
    
    console.log('✅ Task created successfully:', taskRef.id);

    // Trigger new task notification
    await triggerNewTaskNotification({
      taskTitle: taskData.title,
      description: taskData.description,
      assignees: assignees, // Pass the full array of assignees
      dueDate: taskData.dueDate,
      priority: taskData.priority || 'medium',
      createdBy: taskData.createdBy,
      accountId: taskData.accountId
    });

    return {
      id: taskRef.id,
      ...cleanedTask,
      createdAt: new Date()
    };
  } catch (error) {
    console.error('❌ Error creating task:', error);
    throw error;
  }
};

/**
 * Update fulfillment status and trigger notification
 * @param {string} fulfillmentType - The type of fulfillment (technology, hr, marketing, general)
 * @param {Object} fulfillmentData - The fulfillment data
 * @returns {Promise<Object>} - The updated fulfillment with ID
 */
export const updateFulfillment = async (fulfillmentType, fulfillmentData) => {
  try {
    const fulfillment = {
      type: fulfillmentType,
      update: fulfillmentData.update,
      status: fulfillmentData.status,
      assignee: fulfillmentData.assignee,
      dueDate: fulfillmentData.dueDate,
      priority: fulfillmentData.priority || 'medium',
      timestamp: serverTimestamp()
    };

    // Add fulfillment to Firestore
    const fulfillmentRef = await addDoc(collection(db, 'fulfillments'), fulfillment);
    
    console.log(`✅ ${fulfillmentType} fulfillment updated successfully:`, fulfillmentRef.id);

    // Trigger fulfillment notification
    await triggerFulfillmentNotification(fulfillmentType, {
      update: fulfillmentData.update,
      status: fulfillmentData.status,
      assignee: fulfillmentData.assignee,
      dueDate: fulfillmentData.dueDate,
      priority: fulfillmentData.priority || 'medium',
      timestamp: new Date().toISOString()
    });

    return {
      id: fulfillmentRef.id,
      ...fulfillment,
      timestamp: new Date()
    };
  } catch (error) {
    console.error(`❌ Error updating ${fulfillmentType} fulfillment:`, error);
    throw error;
  }
};

/**
 * Create a community post and trigger notification
 * @param {Object} postData - The post data
 * @returns {Promise<Object>} - The created post with ID
 */
export const createCommunityPost = async (postData) => {
  try {
    const post = {
      author: postData.author,
      title: postData.title,
      content: postData.content,
      category: postData.category || 'general',
      timestamp: serverTimestamp(),
      likes: 0,
      comments: 0
    };

    // Add post to Firestore
    const postRef = await addDoc(collection(db, 'community_posts'), post);
    
    console.log('✅ Community post created successfully:', postRef.id);

    // Trigger community post notification
    await triggerCommunityPostNotification({
      author: postData.author,
      title: postData.title,
      content: postData.content,
      category: postData.category || 'general',
      timestamp: new Date().toISOString()
    });

    return {
      id: postRef.id,
      ...post,
      timestamp: new Date()
    };
  } catch (error) {
    console.error('❌ Error creating community post:', error);
    throw error;
  }
};

// ===== EXISTING FUNCTIONS FOR COMPATIBILITY =====

/**
 * Get tasks by user
 */
export const getTasksByUser = async (userId) => {
  try {
    console.log('getTasksByUser called for userId:', userId);
    
    // First, get the user's accountId if they are a client user
    let userAccountId = null;
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        userAccountId = userData.accountId;
        console.log('User accountId:', userAccountId);
      }
    } catch (error) {
      console.log('User not found in users collection or no accountId:', error);
    }

    // Tasks assigned to the user directly OR via their account. One array-contains query
    // per id (clients may only read tasks of their own company: firestore.rules).
    const tasksRef = collection(db, 'tasks');
    const assigneeIds = [...new Set([userId, userAccountId].filter(Boolean))];
    const snapshots = await Promise.all(
      assigneeIds.map((id) => getDocs(query(tasksRef, where('assignees', 'array-contains', id))))
    );
    const byId = new Map();
    snapshots.forEach((snapshot) => snapshot.forEach((doc) => {
      byId.set(doc.id, { id: doc.id, ...doc.data() });
    }));
    const tasks = [...byId.values()];
    
    console.log('Tasks found for user:', tasks.length);
    
    // Sort by creation date (descending)
    return tasks.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
      const dateB = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
      return dateB - dateA;
    });
  } catch (error) {
    console.error('Error fetching tasks by user:', error);
    throw error;
  }
};

/**
 * Get tasks visible to a portal (client) user — excludes internal-only assignments.
 */
export const getTasksForPortalUser = async (userId, accountId = null) => {
  const tasks = await getTasksByUser(userId);
  const filtered = [];

  for (const task of tasks) {
    if (task.assigneeScope === 'internal') continue;

    const assignees = (task.assignees || []).filter(Boolean);
    if (!assignees.length) continue;

    let allInternal = true;
    for (const assignee of assignees) {
      const id = typeof assignee === 'object' ? assignee.id : assignee;
      if (id === userId || (accountId && id === accountId)) {
        allInternal = false;
        break;
      }
      // A refused read is a portal login of another company: still a client assignee
      const userDoc = await getDoc(doc(db, 'users', id)).catch(() => null);
      if (!userDoc || userDoc.exists()) {
        allInternal = false;
        break;
      }
      const internalDoc = await getDoc(doc(db, 'internal_users', id));
      if (!internalDoc.exists()) {
        allInternal = false;
        break;
      }
    }

    if (!allInternal) {
      filtered.push(task);
    }
  }

  return filtered;
};

/**
 * Get tasks by account
 */
export const getTasksByAccount = async (accountId) => {
  try {
    console.log('🔍 getTasksByAccount called with accountId:', accountId);
    const tasksRef = collection(db, 'tasks');
    
    // First try the optimized query with accountId filter and timestamp ordering
    try {
      const q = query(
        tasksRef,
        where('accountId', '==', accountId),
        orderBy('timestamp', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const tasks = [];
      
      querySnapshot.forEach((doc) => {
        tasks.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      console.log(`✅ Found ${tasks.length} tasks with accountId ${accountId} (using timestamp index)`);
      
      // If no tasks found, try fallback query (tasks might not have timestamp field or index issue)
      if (tasks.length === 0) {
        console.log('⚠️ No tasks found with timestamp ordering, trying fallback query...');
        try {
          // Fallback: query by accountId only (no ordering)
          const fallbackQuery = query(
            tasksRef,
            where('accountId', '==', accountId)
          );
          
          const fallbackSnapshot = await getDocs(fallbackQuery);
          const fallbackTasks = [];
          
          fallbackSnapshot.forEach((doc) => {
            const taskData = doc.data();
            fallbackTasks.push({
              id: doc.id,
              ...taskData
            });
          });
          
          console.log(`✅ Fallback query found ${fallbackTasks.length} tasks`);
          
          // Sort by timestamp or createdAt in memory (descending)
          fallbackTasks.sort((a, b) => {
            // Try timestamp first, then createdAt, then fallback to 0
            const timestampA = a.timestamp?.toMillis?.() || a.timestamp || 
                             a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 
                             (a.createdAt ? new Date(a.createdAt).getTime() : 0) || 0;
            const timestampB = b.timestamp?.toMillis?.() || b.timestamp || 
                             b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 
                             (b.createdAt ? new Date(b.createdAt).getTime() : 0) || 0;
            return timestampB - timestampA; // Descending order
          });
          
          return fallbackTasks;
        } catch (fallbackError) {
          console.error('❌ Fallback query also failed:', fallbackError);
          // Last resort: get all tasks and filter in memory
          console.log('🔍 Attempting to get all tasks for debugging...');
          try {
            const debugSnapshot = await getDocs(collection(db, 'tasks'));
            const allTasks = [];
            debugSnapshot.forEach((doc) => {
              const taskData = doc.data();
              if (taskData.accountId === accountId) {
                allTasks.push({
                  id: doc.id,
                  ...taskData
                });
              }
            });
            
            console.log(`🔍 Debug: Found ${allTasks.length} tasks matching accountId ${accountId} out of ${debugSnapshot.size} total tasks`);
            
            // Sort by timestamp or createdAt
            allTasks.sort((a, b) => {
              const timestampA = a.timestamp?.toMillis?.() || a.timestamp || 
                               a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 
                               (a.createdAt ? new Date(a.createdAt).getTime() : 0) || 0;
              const timestampB = b.timestamp?.toMillis?.() || b.timestamp || 
                               b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 
                               (b.createdAt ? new Date(b.createdAt).getTime() : 0) || 0;
              return timestampB - timestampA;
            });
            
            return allTasks;
          } catch (debugError) {
            console.error('❌ Debug query failed:', debugError);
            throw fallbackError; // Throw the original fallback error
          }
        }
      }
      
      return tasks;
    } catch (indexError) {
      // If the index is still building, fall back to a simpler query
      if (indexError.code === 'failed-precondition' || indexError.message?.includes('index')) {
        console.warn('⚠️ Index still building, falling back to simple query for accountId:', accountId);
        
        // Fallback: query by accountId only (no ordering)
        const fallbackQuery = query(
          tasksRef,
          where('accountId', '==', accountId)
        );
        
        const querySnapshot = await getDocs(fallbackQuery);
        const tasks = [];
        
        querySnapshot.forEach((doc) => {
          const taskData = doc.data();
          tasks.push({
            id: doc.id,
            ...taskData
          });
        });
        
        console.log(`🔍 Fallback query found ${tasks.length} tasks. Sample task accountId:`, tasks[0]?.accountId);
        
        // Sort by timestamp or createdAt in memory (descending)
        tasks.sort((a, b) => {
          // Try timestamp first, then createdAt, then fallback to 0
          const timestampA = a.timestamp?.toMillis?.() || a.timestamp || 
                           a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
          const timestampB = b.timestamp?.toMillis?.() || b.timestamp || 
                           b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
          return timestampB - timestampA; // Descending order
        });
        
        console.log(`✅ Found ${tasks.length} tasks with accountId ${accountId} (using fallback query)`);
        return tasks;
      }
      // If it's a different error, throw it
      throw indexError;
    }
  } catch (error) {
    console.error('❌ Error fetching tasks by account:', error);
    // If all else fails, try getting all tasks and filtering in memory (for debugging)
    try {
      console.log('🔍 Attempting to get all tasks for debugging...');
      const allTasksSnapshot = await getDocs(collection(db, 'tasks'));
      const allTasks = [];
      allTasksSnapshot.forEach((doc) => {
        const taskData = doc.data();
        allTasks.push({
          id: doc.id,
          accountId: taskData.accountId,
          title: taskData.title,
          ...taskData
        });
      });
      
      const matchingTasks = allTasks.filter(task => task.accountId === accountId);
      console.log(`🔍 Debug: Found ${matchingTasks.length} tasks matching accountId ${accountId} out of ${allTasks.length} total tasks`);
      console.log('🔍 Sample task accountIds:', [...new Set(allTasks.slice(0, 10).map(t => t.accountId))]);
      
      if (matchingTasks.length > 0) {
        console.log('✅ Returning tasks from debug query');
        return matchingTasks;
      }
    } catch (debugError) {
      console.error('❌ Debug query also failed:', debugError);
    }
    
    throw error;
  }
};

/**
 * Update task
 */
export const updateTask = async (taskId, updateData) => {
  try {
    const taskRef = doc(db, 'tasks', taskId);
    
    // Handle file uploads if files are present
    let processedUpdateData = { ...updateData };
    
    if (updateData.files && Array.isArray(updateData.files)) {
      const fileUploadPromises = updateData.files
        .filter(file => file instanceof File) // Only process File objects
        .map(file => uploadTaskFile(file, taskId));
      
      if (fileUploadPromises.length > 0) {
        try {
          const uploadedFiles = await Promise.all(fileUploadPromises);
          
          // Get existing files from the task (we'll need to fetch this)
          // For now, we'll replace the files array with uploaded file info
          processedUpdateData.files = uploadedFiles;
        } catch (fileError) {
          console.error('Error uploading files:', fileError);
          throw new Error(`Failed to upload files: ${fileError.message}`);
        }
      }
    }
    
    // Clean the data to remove any File objects or other non-serializable data
    const cleanedData = cleanDataForFirestore(processedUpdateData);
    
    await updateDoc(taskRef, {
      ...cleanedData,
      lastUpdated: serverTimestamp()
    });
    
    console.log('✅ Task updated successfully:', taskId);
    return { id: taskId, ...cleanedData };
  } catch (error) {
    console.error('❌ Error updating task:', error);
    throw error;
  }
};

/**
 * Delete task
 */
export const deleteTask = async (taskId) => {
  try {
    const taskRef = doc(db, 'tasks', taskId);
    await deleteDoc(taskRef);
    
    console.log('✅ Task deleted successfully:', taskId);
    return { id: taskId };
  } catch (error) {
    console.error('❌ Error deleting task:', error);
    throw error;
  }
};

/**
 * Toggle task completion
 */
export const toggleTaskCompletion = async (taskId, isCompleted) => {
  try {
    const taskRef = doc(db, 'tasks', taskId);
    await updateDoc(taskRef, {
      completed: isCompleted,
      completedAt: isCompleted ? serverTimestamp() : null,
      lastUpdated: serverTimestamp()
    });
    
    console.log('✅ Task completion toggled:', taskId, isCompleted);
    return { id: taskId, completed: isCompleted };
  } catch (error) {
    console.error('❌ Error toggling task completion:', error);
    throw error;
  }
};

/**
 * Get task comments
 */
export const getTaskComments = async (taskId) => {
  try {
    const commentsRef = collection(db, 'task_comments');
    const q = query(
      commentsRef,
      where('taskId', '==', taskId),
      orderBy('timestamp', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    const comments = [];
    
    querySnapshot.forEach((doc) => {
      comments.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return comments;
  } catch (error) {
    console.error('Error fetching task comments:', error);
    throw error;
  }
};

/**
 * Add task comment
 */
export const addTaskComment = async (taskId, commentData) => {
  try {
    const comment = {
      taskId: taskId,
      content: commentData.content,
      author: commentData.author,
      timestamp: serverTimestamp()
    };

    const commentRef = await addDoc(collection(db, 'task_comments'), comment);
    
    console.log('✅ Task comment added successfully:', commentRef.id);
    return {
      id: commentRef.id,
      ...comment,
      timestamp: new Date()
    };
  } catch (error) {
    console.error('❌ Error adding task comment:', error);
    throw error;
  }
};